// Approvals inbox — the human-in-the-loop review surface. Flows that hit a `human://approve` hook block
// and raise a task here; a reviewer opens one (context + AI recommendation), picks an option + note, and
// resolves it, which unblocks and resumes the flow. Backed by /studio/api/approvals (list/get/resolve).
//
// The inbox is a single column of cards on purpose — a reviewer works top-down and the common case is
// "read the AI recommendation, hit Approve". So: one-click decisions (no dropdown round-trip), how long
// the flow has been blocked shown as an aging badge, context folded away until asked for, and a quiet
// 15s poll so a newly-raised approval appears without anyone pressing Refresh.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader, Card, Button, Select, Alert } from '../ui/primitives';
import { listApprovals, resolveApproval } from '../services/ecapApi';
import CapabilityNotice from '../components/CapabilityNotice';

const STATUS_TABS = [['PENDING', 'Pending'], ['RESOLVED', 'Resolved'], ['', 'All']];
const fmtTime = (ms) => (ms ? new Date(ms).toLocaleString() : '');

// A blocked flow is a customer waiting, so age is the number that matters most on a pending card.
const MIN = 60000, HOUR = 60 * MIN, DAY = 24 * HOUR;
function ago(ms, now) {
  if (!ms) return '';
  const d = Math.max(0, now - ms);
  if (d < MIN) return 'just now';
  if (d < HOUR) return `${Math.floor(d / MIN)}m`;
  if (d < DAY) return `${Math.floor(d / HOUR)}h`;
  return `${Math.floor(d / DAY)}d`;
}
// Waiting an hour is worth noticing; four hours is worth chasing.
const ageTone = (ms, now) => {
  const d = now - (ms || now);
  if (d >= 4 * HOUR) return 'bg-rose-100 text-rose-700';
  if (d >= HOUR) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-500';
};

// Free-text option names come from the flow author, so match on intent rather than an exact list.
const POSITIVE = /^(approve|approved|yes|accept|accepted|allow|ok|okay|confirm|grant|proceed)$/i;
const NEGATIVE = /^(deny|denied|reject|rejected|no|decline|declined|refuse|block|cancel)$/i;
const optionVariant = (o) => (POSITIVE.test(o) ? 'emerald' : NEGATIVE.test(o) ? 'danger' : 'outline');
const optionIcon = (o) => (POSITIVE.test(o) ? '✓ ' : NEGATIVE.test(o) ? '✕ ' : '');

// BUG-49: a resolved card showed the AI's pre-decision value and the human's decision near each other with no
// label, so you couldn't tell whether the reviewer FOLLOWED or OVERRODE the AI. Classify the relationship so the
// card can say so. Returns 'followed' | 'overrode' | null (null when there's nothing to compare).
export function decisionAgreement(aiRecommendation, decision) {
  const ai = (aiRecommendation || '').trim();
  const hu = (decision || '').trim();
  if (!ai || !hu) return null;
  if (ai.toLowerCase() === hu.toLowerCase()) return 'followed';
  // Same sentiment class (approve≈yes, deny≈reject) still counts as following the AI's direction.
  if (POSITIVE.test(ai) && POSITIVE.test(hu)) return 'followed';
  if (NEGATIVE.test(ai) && NEGATIVE.test(hu)) return 'followed';
  if ((POSITIVE.test(ai) && NEGATIVE.test(hu)) || (NEGATIVE.test(ai) && POSITIVE.test(hu))) return 'overrode';
  return 'overrode'; // differing free-text options → treat as an override (the reviewer chose a different option)
}

function ApprovalCard({ task, editable, now, onResolved }) {
  const pending = task.status === 'PENDING';
  const opts = task.options && task.options.length ? task.options : ['approve', 'deny'];
  const [selected, setSelected] = useState(opts[0]); // only used in the many-options (Select) form
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null); // the option currently being submitted
  const [err, setErr] = useState(null);
  const [showContext, setShowContext] = useState(false);

  const resolve = async (decision) => {
    setBusy(decision); setErr(null);
    // APPROVALS-STUCK-BUSY-ON-QUIET-REFETCH-FAIL: clear busy in finally, not only on error — the resolve
    // succeeded on the backend, so even if onResolved()'s quiet refetch fails the card must re-enable rather
    // than stay stuck on "Resolving…". A duplicate resolve is guarded server-side (atomic claimPending).
    try { await resolveApproval(task.id, decision, note); onResolved(); }
    // approvals-resolve-no-status-branch: a 409 means the task was already resolved (someone else, or a
    // double-submit that raced) — the backend guards it atomically. Say so and refresh the card instead of
    // showing a raw backend error the reviewer can't act on.
    catch (e) {
      if (e.status === 409) { setErr('Already resolved by someone else — refreshing…'); onResolved(); }
      else setErr(e.message || 'Could not resolve');
    }
    finally { setBusy(null); }
  };

  const snapshot = Object.entries(task.contextSnapshot || {}).filter(([k]) => !k.startsWith('_'));
  // Up to three options fit as buttons; beyond that fall back to a picker so the card doesn't sprawl.
  const asButtons = opts.length <= 3;

  return (
    <Card className={`mb-3 ${pending ? '' : 'opacity-90'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">{task.title || task.id}</div>
          <div className="text-[12px] text-slate-400 truncate">
            {task.flowId}
            {task.sessionId ? <span className="text-slate-300"> · {task.sessionId}</span> : null}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {pending && (
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${ageTone(task.createdAt, now)}`}
              title={`Raised ${fmtTime(task.createdAt)}`}>
              ⏱ {ago(task.createdAt, now)}
            </span>
          )}
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${pending ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {pending ? 'pending' : (task.decision || 'resolved')}
          </span>
        </div>
      </div>

      {task.aiRecommendation && (
        <div className="mt-2 text-[12px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-2 py-1.5">
          🧠 AI recommendation: {task.aiRecommendation}
        </div>
      )}

      {snapshot.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setShowContext((s) => !s)}
            className="text-[12px] text-slate-500 hover:text-slate-700">
            {showContext ? '▾' : '▸'} context ({snapshot.length})
          </button>
          {showContext && (
            <div className="mt-1.5 rounded-md bg-slate-50 border border-slate-100 px-2.5 py-2 grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
              {snapshot.map(([k, v]) => (
                <div key={k} className="text-[12px] text-slate-600 break-words"><span className="text-slate-400">{k}:</span> {v}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {pending && editable ? (
        <div className="mt-3 border-t border-slate-100 pt-3 flex flex-wrap items-center gap-2">
          <input className="flex-1 min-w-[200px] rounded-md border border-slate-200 px-2.5 py-1.5 text-sm" value={note}
            onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — reason / context for the customer" />
          {asButtons ? opts.map((o) => (
            <Button key={o} size="sm" variant={optionVariant(o)} disabled={!!busy} onClick={() => resolve(o)}>
              {busy === o ? 'Resolving…' : `${optionIcon(o)}${o}`}
            </Button>
          )) : (
            <>
              <Select value={selected} onChange={(e) => setSelected(e.target.value)}>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Button size="sm" disabled={!!busy} onClick={() => resolve(selected)}>
                {busy ? 'Resolving…' : 'Resolve & resume'}
              </Button>
            </>
          )}
        </div>
      ) : !pending && (
        <div className="mt-2 text-[12px] text-slate-500 border-t border-slate-100 pt-2">
          {/* BUG-49: label the AI's recommendation and the human's decision explicitly, and say whether the
              reviewer followed or overrode the AI — so the two values are never mistaken for one another. */}
          {task.aiRecommendation && task.decision ? (
            <div className="mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="text-slate-400">🧠 AI recommended</span>
              <b className="text-indigo-700">{task.aiRecommendation}</b>
              <span className="text-slate-300">→</span>
              <span className="text-slate-400">👤 human decided</span>
              <b className="text-slate-700">{task.decision}</b>
              {decisionAgreement(task.aiRecommendation, task.decision) === 'overrode' ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">overrode AI</span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">followed AI</span>
              )}
            </div>
          ) : (
            <div>{task.decision ? <>Decided <b>{task.decision}</b></> : 'Resolved'}</div>
          )}
          {(task.resolutionNote || task.resolvedBy || task.resolvedAt) && (
            <div className="text-slate-400">
              {task.resolutionNote ? `“${task.resolutionNote}”` : ''}
              {task.resolvedBy ? ` · by ${task.resolvedBy}` : ''}
              {task.resolvedAt ? ` · ${fmtTime(task.resolvedAt)}` : ''}
            </div>
          )}
        </div>
      )}
      {err && <div className="mt-2 text-[12px] text-rose-600">{err}</div>}
    </Card>
  );
}

export default function OpsApprovals({ authSession, readOnly, capability }) {
  const [status, setStatus] = useState('PENDING');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [flow, setFlow] = useState('');
  const [now, setNow] = useState(() => Date.now());

  // approvals-load-race: monotonic token so a slow response for one tab (or the 15s poll) can't land last
  // and overwrite a newer tab's list. `shown` filters only by flow/search — NOT status — so whichever
  // response commits last renders under the current tab; without this, a stale RESOLVED response could wipe
  // the PENDING list (waiting approvals appear to vanish for ~15s).
  const reqSeq = useRef(0);
  // `quiet` refreshes skip the spinner: the 15s poll must not make the page flicker under a reviewer.
  const load = useCallback(async (quiet) => {
    const seq = ++reqSeq.current;
    if (!quiet) setLoading(true);
    try {
      const data = await listApprovals(status, authSession) || [];
      if (seq === reqSeq.current) { setTasks(data); setError(null); }
    }
    catch (e) { if (!quiet && seq === reqSeq.current) setError(e.message || 'Could not load approvals'); }
    finally { if (!quiet) setLoading(false); }
  }, [status, authSession]);

  useEffect(() => { load(); }, [load]);

  // A blocked flow is a customer waiting, so new tasks should surface on their own. The age badges
  // re-tick off the same interval. Held in a ref so switching tabs doesn't restart the timer.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const h = setInterval(() => { setNow(Date.now()); loadRef.current(true); }, 15000);
    return () => clearInterval(h);
  }, []);

  const flows = useMemo(() => [...new Set(tasks.map((t) => t.flowId).filter(Boolean))].sort(), [tasks]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tasks.filter((t) => {
      if (flow && t.flowId !== flow) return false;
      if (!needle) return true;
      const hay = [t.title, t.flowId, t.sessionId, t.aiRecommendation, t.decision, t.resolutionNote,
        ...Object.values(t.contextSnapshot || {})].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [tasks, q, flow]);

  const pendingCount = tasks.filter((t) => t.status === 'PENDING').length;
  const filtered = shown.length !== tasks.length;
  return (
    <div className="p-6">
      <PageHeader title={pendingCount > 0 ? `Approvals · ${pendingCount} pending` : 'Approvals'}
        subtitle="Human-in-the-loop reviews — flows waiting on a decision"
        right={<Button size="sm" variant="outline" onClick={() => load()} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button>} />
      <CapabilityNotice capability={capability} kind="approvals" />
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex items-center gap-1">
            {STATUS_TABS.map(([val, label]) => (
              <button key={label} onClick={() => setStatus(val)}
                className={`text-sm px-3 py-1.5 rounded-md ${status === val ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {label}{val === 'PENDING' && status === 'PENDING' && pendingCount > 0 ? ` (${pendingCount})` : ''}
              </button>
            ))}
          </div>
          <input className="ml-auto w-full sm:w-64 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm" value={q}
            onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search title, context, session…" />
          {flows.length > 1 && (
            <Select value={flow} onChange={(e) => setFlow(e.target.value)}>
              <option value="">All flows</option>
              {flows.map((f) => <option key={f} value={f}>{f}</option>)}
            </Select>
          )}
        </div>

        {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
        {!loading && shown.length === 0 && (
          <div className="text-sm text-slate-400 py-10 text-center">
            {filtered || q || flow ? 'Nothing matches that filter.'
              : status === 'PENDING' ? 'No pending approvals — nothing is waiting on a human right now.' : 'Nothing here.'}
          </div>
        )}
        {shown.map((t) => (
          // approvals-swallowed-post-resolve-reload: use a NON-quiet reload after a resolve so a failed
          // confirming refetch surfaces in the banner (a quiet one was swallowed, leaving the card stale).
          <ApprovalCard key={t.id} task={t} editable={!readOnly} now={now} onResolved={() => load()} />
        ))}
      </div>
    </div>
  );
}
